import React, { createContext, useContext, useState, ReactNode } from 'react';

export type Language = 'mr' | 'hi' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  translations: Record<string, Record<Language, string>>;
}

const defaultTranslations = {
  // Navigation and Common
  home: {
    mr: 'मुख्यपृष्ठ',
    hi: 'मुख्य पृष्ठ',
    en: 'Home',
  },
  aboutUs: {
    mr: 'आमच्याबद्दल',
    hi: 'हमारे बारे में',
    en: 'About Us',
  },
  departments: {
    mr: 'विभाग',
    hi: 'विभाग',
    en: 'Departments',
  },
  services: {
    mr: 'सेवा',
    hi: 'सेवाएं',
    en: 'Services',
  },
  schemes: {
    mr: 'योजना',
    hi: 'योजनाएं',
    en: 'Schemes',
  },
  gallery: {
    mr: 'गॅलरी',
    hi: 'गैलरी',
    en: 'Gallery',
  },
  adarshtakta: {
    mr: 'आदर्शतक्ता',
    hi: 'आदर्शता',
    en: 'Ideal Status',
  },
  contact: {
    mr: 'संपर्क',
    hi: 'संपर्क',
    en: 'Contact',
  },
  adminLogin: {
    mr: 'प्रशासक लॉगिन',
    hi: 'प्रशासक लॉगिन',
    en: 'Admin Login',
  },
  // Footer translations
  usefulLinks: {
    mr: 'उपयुक्त दुवे',
    hi: 'उपयोगी लिंक',
    en: 'Useful Links',
  },
  followUs: {
    mr: 'आम्हाला फॉलो करा',
    hi: 'हमें फॉलो करें',
    en: 'Follow Us',
  },
  copyright: {
    mr: 'सर्व हक्क राखीव',
    hi: 'सर्वाधिकार सुरक्षित',
    en: 'All Rights Reserved',
  },
  quickLinks: {
    mr: 'जलद दुवे',
    hi: 'त्वरित लिंक',
    en: 'Quick Links',
  },
  // Contact page translations
  contactUs: {
    mr: 'आमच्याशी संपर्क साधा',
    hi: 'हमसे संपर्क करें',
    en: 'Contact Us',
  },
  address: {
    mr: 'पत्ता',
    hi: 'पता',
    en: 'Address',
  },
  phoneNumber: {
    mr: 'दूरध्वनी क्रमांक',
    hi: 'फोन नंबर',
    en: 'Phone Number',
  },
  emailAddress: {
    mr: 'ईमेल पत्ता',
    hi: 'ईमेल पता',
    en: 'Email Address',
  },
  sendMessage: {
    mr: 'संदेश पाठवा',
    hi: 'संदेश भेजें',
    en: 'Send Message',
  },
  submit: {
    mr: 'पाठवा',
    hi: 'भेजें',
    en: 'Submit',
  }
};

export const LanguageContext = createContext<LanguageContextType>({
  language: 'mr',
  setLanguage: () => {},
  translations: defaultTranslations,
});

export const useLanguage = () => useContext(LanguageContext);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const [language, setLanguage] = useState<Language>('mr');

  const value = {
    language,
    setLanguage,
    translations: defaultTranslations,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
